import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BrandMultiSelect, NumberField, SectionCard, parseNumberInput } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { systemJson } from '../../pages/adminSystem/api';

const KINDS = [
  'price_changes_auto_applied',
  'suspicious_price_blocked',
  'products_discontinued',
  'price_refresh_failed',
  'source_down',
  'zero_products',
] as const;

type Kind = (typeof KINDS)[number];
type Mode = 'immediate' | 'hourly' | 'daily';

interface AlertSettings {
  modes: Record<Kind, Mode>;
  dailySendHourPt: number;
  urgentBrands: string[];
  reminderIntervalHours: number;
}

interface AlertSettingsResponse {
  settings: AlertSettings;
  defaults: AlertSettings;
}

export interface AlertSettingsEmbedState {
  dirty: boolean;
  invalid: boolean;
  save: () => Promise<void>;
  discard: () => void;
}

const MODES: Mode[] = ['immediate', 'hourly', 'daily'];

/**
 * System settings for Motzu Alert delivery (MOT-797). Hidden while
 * `admin_alert_digest` is off. On the rebuilt System page the tab save bar
 * owns the save (`onState`); the legacy General tab saves from this card.
 */
export const AdminAlertSettingsCard: React.FC<{ onState?: (state: AlertSettingsEmbedState | null) => void }> = ({ onState }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { enabled, isLoading } = useOrgFeatureFlag('admin_alert_digest');
  const query = useQuery({
    queryKey: ['admin-alert-settings'],
    enabled,
    queryFn: () => systemJson<AlertSettingsResponse>('/admin/system/alert-settings'),
  });

  const [modes, setModes] = useState<Record<Kind, Mode> | null>(null);
  const [hour, setHour] = useState('8');
  const [reminder, setReminder] = useState('6');
  const [urgent, setUrgent] = useState<string[]>([]);

  useEffect(() => {
    const saved = query.data?.settings;
    if (!saved) return;
    setModes({ ...saved.modes });
    setHour(String(saved.dailySendHourPt));
    setReminder(String(saved.reminderIntervalHours));
    setUrgent([...saved.urgentBrands]);
  }, [query.data]);

  const saved = query.data?.settings;
  const parsedHour = parseNumberInput(hour);
  const parsedReminder = parseNumberInput(reminder);
  const hourOk = parsedHour != null && Number.isInteger(parsedHour) && parsedHour >= 0 && parsedHour <= 23;
  const reminderOk = parsedReminder != null && Number.isInteger(parsedReminder) && parsedReminder >= 1 && parsedReminder <= 72;
  const invalid = !hourOk || !reminderOk;
  const modesDirty = Boolean(saved && modes && KINDS.some((kind) => modes[kind] !== saved.modes[kind]));
  const urgentDirty = Boolean(
    saved && (urgent.length !== saved.urgentBrands.length || urgent.some((brand, index) => brand !== saved.urgentBrands[index])),
  );
  const dirty = Boolean(saved && modes) && (modesDirty || urgentDirty || hour !== String(saved?.dailySendHourPt) || reminder !== String(saved?.reminderIntervalHours));

  const save = useMutation({
    mutationFn: async () => {
      if (!modes || !hourOk || !reminderOk || parsedHour == null || parsedReminder == null) {
        throw new Error(t('admin.alertSettings.invalid'));
      }
      return systemJson<{ settings: AlertSettings }>('/admin/system/alert-settings', {
        method: 'PUT',
        body: JSON.stringify({
          modes,
          dailySendHourPt: parsedHour,
          reminderIntervalHours: parsedReminder,
          urgentBrands: urgent,
        }),
      });
    },
    onSuccess: () => {
      if (!onStateRef.current) showToast(t('admin.alertSettings.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['admin-alert-settings'] });
    },
    onError: (error: Error) => {
      if (!onStateRef.current) showToast(error.message, 'error');
    },
  });

  const discard = () => {
    if (!saved) return;
    setModes({ ...saved.modes });
    setHour(String(saved.dailySendHourPt));
    setReminder(String(saved.reminderIntervalHours));
    setUrgent([...saved.urgentBrands]);
  };

  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const saveRef = useRef(save.mutateAsync);
  saveRef.current = save.mutateAsync;
  const discardRef = useRef(discard);
  discardRef.current = discard;

  useEffect(() => {
    if (!onStateRef.current) return;
    if (!enabled) {
      onStateRef.current(null);
      return;
    }
    onStateRef.current({
      dirty,
      invalid,
      save: async () => {
        await saveRef.current();
      },
      discard: () => discardRef.current(),
    });
  }, [enabled, dirty, invalid]);

  if (isLoading || !enabled || !modes) return null;

  return (
    <SectionCard dense title={t('admin.alertSettings.title')}>
      <Typography sx={{ fontSize: 13, color: tokens.ink.muted, mb: 2 }}>{t('admin.alertSettings.help')}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 220px' }, gap: 1.25, alignItems: 'center' }}>
        {KINDS.map((kind) => (
          <React.Fragment key={kind}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t(`admin.alertSettings.kinds.${kind}`)}</Typography>
            <TextField
              select
              size="small"
              value={modes[kind]}
              onChange={(event) => setModes((current) => (current ? { ...current, [kind]: event.target.value as Mode } : current))}
              aria-label={t(`admin.alertSettings.kinds.${kind}`)}
            >
              {MODES.map((mode) => (
                <MenuItem key={mode} value={mode}>{t(`admin.alertSettings.${mode}`)}</MenuItem>
              ))}
            </TextField>
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <NumberField
          integer
          min={0}
          max={23}
          value={hour}
          onChange={setHour}
          label={t('admin.alertSettings.dailyHour')}
          helperText={t('admin.alertSettings.dailyHourHelp')}
          aria-label={t('admin.alertSettings.dailyHour')}
        />
        <NumberField
          integer
          min={1}
          max={72}
          value={reminder}
          onChange={setReminder}
          label={t('admin.alertSettings.reminder')}
          helperText={t('admin.alertSettings.reminderHelp')}
          aria-label={t('admin.alertSettings.reminder')}
        />
      </Box>
      <Typography sx={{ mt: 2, mb: 1, fontSize: 13.5, fontWeight: 600 }}>{t('admin.alertSettings.urgent')}</Typography>
      <BrandMultiSelect
        value={urgent}
        onChange={setUrgent}
        placeholder={t('admin.alertSettings.urgentPlaceholder')}
        notListedMessage={t('admin.alertSettings.notListed')}
        aria-label={t('admin.alertSettings.urgent')}
      />
      <Typography sx={{ mt: 1, fontSize: 13, color: tokens.ink.muted }}>{t('admin.alertSettings.urgentHelp')}</Typography>
      {!onState && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" disabled={!dirty || invalid || save.isPending} onClick={() => save.mutate()}>
            {t('admin.alertSettings.save')}
          </Button>
        </Box>
      )}
    </SectionCard>
  );
};
